import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { Observable, from, Subject, Subscription, EMPTY, forkJoin, of } from 'rxjs'
import { concatMap, distinctUntilChanged, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { EntityConfiguration, AttachmentComponent } from '../../../models/entity-configuration'
import { Field } from '../../../models/field'
import { root } from '../../../../permissions/permissions'
import { JmeleonActionsPermissionService } from '../../../../permissions/services/jmeleon-actions-permission.service'
import { SettingsService } from '../../../../settings/services/settings.service'
import { StoredEntityService } from '../../../../entity-store-access/services/stored-entity.service'
import { isEqual } from 'lodash'
import { EntityConfigService } from '../../../services/entity-config.service'
import { StoredEntityStateService } from '../../../../entity-store-access/services/stored-entity-state.service'

@Component({
  selector: 'jml-lib-entity-field-group',
  templateUrl: './entity-field-group.component.html',
  styleUrls: ['./entity-field-group.component.scss']
})
export class EntityFieldGroupComponent implements OnInit, OnChanges {
  @ViewChild('table', { read: ElementRef, static: false }) tableRef: ElementRef

  // @Input()
  configName: string
  @Input() attachmentComponent: AttachmentComponent
  // @Input()
  fieldNames: string[]
  // @Input()
  entryId: number
  @Input() refreshTrigger: Subject<[string, number]>

  data
  configuration: EntityConfiguration
  fieldsInGroup$: Observable<Field[]> = EMPTY
  editableFields = []
  currency: string
  subscriptions: Subscription[] = []

  constructor(
    private storedEntityService: StoredEntityService,
    private japs: JmeleonActionsPermissionService,
    private settingsService: SettingsService,
    private entityConfigService: EntityConfigService,
    private storedEntityWrapper: StoredEntityStateService
  ) {}

  ngOnInit(): void {
    if (!this.attachmentComponent) {
      return
    }
    this.fieldNames = this.attachmentComponent.fields

    this.settingsService
      .getSetting('CURRENCY')
      .pipe(map((setting) => setting.value))
      .subscribe((currency) => (this.currency = currency))

    // Get observable for entity configuration
    const configuration$: Observable<EntityConfiguration> = this.storedEntityWrapper.entity$.pipe(
      filter((entity) => !!entity),
      map((entity) => entity.type),
      tap((configName) => (this.configName = configName)),
      switchMap((configName) => this.entityConfigService.getConfig$(configName)),
      distinctUntilChanged((a, b) => isEqual(a, b))
    )

    const workflow = configuration$.pipe(
      filter((config) => !!config),
      tap((config) => {
        this.configuration = config
      }),
      switchMap((config) => {
        return this.storedEntityWrapper.entity$.pipe(
          filter((entity) => !!entity),
          map((entity) => entity.fields),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          tap((data) => (this.data = data)),
          tap((data) => (this.entryId = data['id'])),
          map((_) => config)
        )
      }),
      switchMap(() => from(this.fieldNames)),
      map((fieldName) => this.configuration.fields.find((field) => field.field === fieldName)),
      mergeMap((field) => {
        return this.japs
          .userHasPermissionForAction(root.dto.$dtoType.$dtoField.read, {
            $dtoType: this.configuration.type,
            $dtoField: field.field
          })
          .pipe(
            filter((userHasReadPermission) => userHasReadPermission),
            map((_) => field)
          )
      }),
      mergeMap((field) => {
        // aggregate editableFields[] (for updateEntity)
        const requestedPermissions = [
          root.dto.$dtoType.create,
          root.dto.$dtoType.write,
          root.dto.$dtoType.$dtoField.write
        ]

        return this.japs
          .userHasPermissionForActions(requestedPermissions, {
            $dtoType: this.configuration.type,
            $dtoField: field.field
          })
          .pipe(
            tap((permissions) => {
              if (field.editable) {
                if (permissions[root.dto.$dtoType.write] && permissions[root.dto.$dtoType.$dtoField.write]) {
                  this.editableFields.push(field.field)
                }
              }
            })
          )
      })
    )

    this.fieldsInGroup$ = configuration$.pipe(
      filter((config) => !!config),
      tap((config) => {
        this.configuration = config
      }),
      switchMap((config) => {
        return this.storedEntityWrapper.entity$.pipe(
          filter((entity) => !!entity),
          map((entity) => entity.fields),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          tap((data) => (this.data = data)),
          tap((data) => (this.entryId = data['id'])),
          map((_) => config)
        )
      }),
      concatMap((config) =>
        of(config.fields).pipe(
          mergeMap((fields) => {
            const existingFields = this.fieldNames
              .map((fieldName) => fields.find((f) => f.field === fieldName))
              .filter((field) => !!field)

            return forkJoin(
              existingFields.map((field) =>
                this.japs
                  .userHasPermissionForAction(root.dto.$dtoType.$dtoField.read, {
                    $dtoType: config.type,
                    $dtoField: field.field
                  })
                  .pipe(
                    filter((userHasReadPermission) => userHasReadPermission),
                    map((_) => field)
                  )
              )
            )
          })
        )
      )
    )

    this.subscriptions.push(workflow.subscribe())
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.entryId?.isFirstChange()) {
      this.subscriptions.forEach((sub) => sub.unsubscribe())
      this.ngOnInit()
    }
  }

  afterTableInit() {
    this.tableRef?.nativeElement.addEventListener(
      'click',
      (event: MouseEvent) => {
        if (event.target['id'] === 'cancelRowSelection') {
          // If the target indicates a cancellation of a row selection event, stop its propagation to prevent row selection
          event.preventDefault()
          event.stopPropagation()
          document.dispatchEvent(new MouseEvent('click'))
          // dispatch new event to target
          event.target.dispatchEvent(new CustomEvent('edit'))
        }
      },
      true
    )
  }

  onRowEditDelete(field) {
    const obj = {}
    obj[field.field] = null
    this.storedEntityService
      .updateEntity$(this.configuration.fields, this.configName, this.entryId, obj)
      .subscribe((entity) => {
        this.data[field.field] = entity[field.field]
        this.refreshTrigger?.next([this.configName, this.entryId])
      })
  }

  completeCellEdit(event) {
    const eventRowData = event.data
    const fieldName = event.field

    const update = new Object()
    update[fieldName] = eventRowData[fieldName]

    this.storedEntityService
      .updateEntity$(this.configuration.fields, this.configName, this.entryId, update, eventRowData['_lastUpdate_'])
      .subscribe((entity) => {
        this.data[fieldName] = entity.fields[fieldName]
        this.refreshTrigger?.next([this.configName, this.entryId])
      })
  }

  isEditable(field: Field): boolean {
    return this.editableFields.includes(field.field)
  }

  _isCatalogueEntry(field: Field): boolean {
    return Field.isCatalogueEntry(field)
  }

  _isEntityReference(field: Field): boolean {
    return Field.isEntityReference(field)
  }
}
